import React, { useEffect, useState, useImperativeHandle, forwardRef, useRef, useMemo } from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';

import { AdminNavigation, Button, Card, EmptyComponent, ErrorComponent, FilterSection, IconButton, PageHeader, Select, Spinner, TableComponents, TextInput, Typography } from '../../components';
import * as S from './AdminAdmins.styles';
import { META_TITLE, ROLES, USER_SORT_OPTIONS } from '../../../constants/general.constants';
import { useHistory } from 'react-router-dom';
import { useDebouncedFn } from 'beautiful-react-hooks';
import { useFlexLayout, useTable } from 'react-table';
import { fetchUsers } from '../../services/users';
import { inviteAdmin } from '../../services/invites';
import { errorHandler } from '../../services/authService';
import { pattern, required } from '../../utils/form-default-errors';
import { EMAIL } from '../../utils/common-regex';

const ADMIN_ROLES = [
	{ label: 'Admin', value: ROLES.ADMIN },
	{ label: 'Super Admin', value: ROLES.SUPER },
];

const AddAdminModal = forwardRef(({ onSuccessCallback }, ref) => {
	const [isOpen, setIsOpen] = useState(false);

	const {
		handleSubmit,
		register,
		reset,
		setValue,
		control,
		formState: { errors, isSubmitting },
	} = useForm();

	const handleOpenModal = () => {
		setIsOpen(true);
	};

	const handleCloseModal = () => {
		setIsOpen(false);
		setTimeout(() => {
			reset();
		}, 200);
	};

	async function asyncCaller(data) {
		await inviteAdmin(data);
		onSuccessCallback();
		handleCloseModal();
	}

	const onSubmit = (e) => {
		handleSubmit(asyncCaller)(e).catch(errorHandler);
	};

	useImperativeHandle(
		ref,
		() => ({
			open: handleOpenModal,
			close: handleCloseModal,
		}),
		[handleOpenModal, handleCloseModal]
	);

	return (
		<ReactModal ref={ref} className="add-user-modal" closeTimeoutMS={200} isOpen={isOpen} onRequestClose={handleCloseModal}>
			<S.Form onSubmit={onSubmit}>
				<IconButton className="close-button" type="button" icon={['fal', 'times']} onClick={handleCloseModal} />
				<div className="modal-content">
					<header className="modal-content__header">
						<Typography tag="h2" weight="extrablack" center>
							Add Admin
						</Typography>
					</header>

					<section className="modal-content__section">
						<TextInput
							label="First Name"
							placeholder="First Name"
							error={errors.firstName}
							{...register('firstName', {
								required: required('First Name'),
							})}
						/>

						<TextInput
							label="Last Name"
							placeholder="Last Name"
							error={errors.lastName}
							{...register('lastName', {
								required: required('Last Name'),
							})}
						/>

						<TextInput
							label="Email"
							placeholder="Email"
							error={errors.email}
							{...register('email', {
								required: required('Email'),
								pattern: pattern('Email', EMAIL),
							})}
						/>

						<Controller
							name="role"
							control={control}
							rules={{ required: required('Role') }}
							render={({ field: { onChange, value } }) => <Select label="Role" id="role" error={errors?.role} placeholder="Role" options={ADMIN_ROLES} value={ADMIN_ROLES.find((c) => c.value === value) || ''} onChange={(val) => onChange(val.value)} />}
						/>
					</section>

					<div className="modal-footer">
						<Button variant="outline" variation="secondary" type="button" onClick={handleCloseModal}>
							<Typography variation="button-medium" weight="bold">
								Cancel
							</Typography>
						</Button>
						<div className="flex-1" />
						<Button>
							<Typography variation="button-medium" weight="bold">
								{isSubmitting ? 'Loading' : 'Add'}
							</Typography>
						</Button>
					</div>
				</div>
			</S.Form>
		</ReactModal>
	);
});

AddAdminModal.displayName = 'AddAdminModal';

AddAdminModal.propTypes = {
	onSuccessCallback: PropTypes.func.isRequired,
};

const AdminAdmins = () => {
	const history = useHistory();
	const [pageStatus, setPageStatus] = useState('idle');
	const [adminUsers, setAdminUsers] = useState([]);

	const initialPageNumber = 0;
	const [pageNumber, setPageNumber] = useState(initialPageNumber);
	const [totalPages, setTotalPages] = useState(undefined);
	const [loadingMore, setLoadingMore] = useState(false);

	const filterRef = useRef();
	const AddAdminModalRef = useRef();

	const onChangeCallback = useDebouncedFn(async () => {
		setPageStatus('loading');
		try {
			const {
				data: { result },
			} = await fetchUsers({ page: initialPageNumber, role: ROLES.ADMIN, ...filterRef.current.value });
			setAdminUsers(result.users);
			setPageNumber(initialPageNumber);
			setPageStatus('success');
		} catch (error) {
			setPageStatus('error');
		}
	}, 300);

	async function loadMore() {
		setLoadingMore(true);
		try {
			if (totalPages > pageNumber + 1) {
				const {
					data: { result },
				} = await fetchUsers({ page: pageNumber + 1, role: ROLES.ADMIN, ...filterRef.current.value });
				setAdminUsers((prev) => [...prev, ...result.users]);
				setTotalPages(result.totalPages);
				setPageNumber((prev) => prev + 1);
			} else {
				toast.error('No more resources to load');
			}
			setLoadingMore(false);
		} catch (error) {
			setLoadingMore(false);
		}
	}

	async function initializeData() {
		setPageStatus('loading');
		try {
			const {
				data: { result },
			} = await fetchUsers({ page: initialPageNumber, role: ROLES.ADMIN });
			setAdminUsers(result.users);
			setTotalPages(result.totalPages);
			setPageStatus('success');
		} catch (error) {
			setPageStatus('error');
		}
	}

	useEffect(() => {
		// Load initial data
		if (pageStatus === 'idle') {
			initializeData();
		}
	}, [pageStatus]);

	const handleAddUser = () => AddAdminModalRef.current.open();

	useEffect(() => {
		// Set document title
		document.title = `Admins | ${META_TITLE}`;
	}, []);

	const columns = useMemo(
		() => [
			{
				Header: 'First Name',
				accessor: 'firstName',
			},
			{
				Header: 'Last Name',
				accessor: 'lastName',
			},
			{
				Header: 'Email',
				accessor: 'email',
			},
			{
				Header: 'Role',
				id: 'role',
				accessor: (originalRow) => {
					return originalRow.role === ROLES.ADMIN ? 'Admin' : 'Super Admin';
				},
			},
			{
				Header: '# of Templates',
				accessor: 'templateCreatedCount',
			},
		],
		[]
	);
	const data = React.useMemo(() => adminUsers, [adminUsers]);

	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
		{
			columns,
			data,
		},
		useFlexLayout
	);

	const renderContent = () => {
		switch (pageStatus) {
			case 'loading':
				return <Spinner />;
			case 'error':
				return <ErrorComponent />;
			case 'success':
				return rows.length > 0 ? (
					<table {...getTableProps()}>
						<thead>
							{headerGroups.map(({ key, ...headerGroup }) => (
								<tr key={key} {...headerGroup.getHeaderGroupProps()}>
									{headerGroup.headers.map(({ key, ...column }) => (
										<th key={key} {...column.getHeaderProps()}>
											<Typography tag="h6" weight="semibold">
												{column.render('Header')}
											</Typography>
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody {...getTableBodyProps()}>
							{rows.map(({ key, ...row }) => {
								prepareRow(row);
								return (
									<TableComponents.ClickableRow
										key={key}
										{...row.getRowProps()}
										onClick={() => {
											history.push(`/admin/admins/${row.original.id}`);
										}}
									>
										{row.cells.map(({ key, ...cell }) => {
											return (
												<td key={key} className="overflow-hidden" {...cell.getCellProps()}>
													<Typography tag="p" variation="2">
														{cell.render('Cell')}
													</Typography>
												</td>
											);
										})}
									</TableComponents.ClickableRow>
								);
							})}
						</tbody>
					</table>
				) : (
					<EmptyComponent title="No current admin users" message="Invite a new admin user to get started." icon={['fal', 'users']} />
				);
			default:
				return null;
		}
	};

	return (
		<AdminNavigation>
			<PageHeader title="Admins" action={[{ id: 2, onClick: handleAddUser, label: 'Add Admin', icon: ['fal', 'plus'] }]} />
			<AddAdminModal ref={AddAdminModalRef} onSuccessCallback={onChangeCallback} />
			<S.Wrapper>
				<FilterSection
					ref={filterRef}
					onChangeCallback={onChangeCallback}
					initialState={{
						searchText: '',
						sortOption: USER_SORT_OPTIONS[0],
						showAdvancedFilters: false,
						activeFilters: [],
						distance: 0,
						counties: [],
						locationTypes: [],
						categories: [],
						careerClusters: [],
						ageGroups: [],
						employabilitySkills: [],
						curriculumStandards: [],
					}}
					sortOptions={USER_SORT_OPTIONS}
					showFilters={false}
				/>

				<Card>
					<TableComponents.TableWrapper>{renderContent()}</TableComponents.TableWrapper>
				</Card>
				{adminUsers?.length > 0 && totalPages > pageNumber + 1 && (
					<S.LoadMoreWrapper>
						<Button variant="outline" variation="secondary" type="button" onClick={loadMore}>
							<Typography variation="button-medium" weight="bold">
								{loadingMore ? 'Loading...' : 'Load More'}
							</Typography>
						</Button>
					</S.LoadMoreWrapper>
				)}
			</S.Wrapper>
		</AdminNavigation>
	);
};

export default AdminAdmins;
